import { useCallback, useEffect, useRef, useState } from "react";

import { onboardingAgentPreferencesService } from "../../onboardingAgentPreferencesService";
import { onboardingAgentTrackingService } from "../../onboardingAgentTrackingService";
import {
    createDefaultTriggerAnchor,
    createTriggerAnchorFromPosition,
    resolveTriggerAnchorToPosition,
} from "../helpers/OnboardingAgentAnchor.helpers";
import {
    getCompactPanelHeight,
    getExpandedPanelHeight,
    resolvePanelPlacement,
} from "../helpers/OnboardingAgentPosition.helpers";
import type { OnboardingAgentTriggerAnchor, OnboardingAgentViewportPosition } from "../OnboardingAgent.types";

type UseOnboardingAgentParams = {
    readonly getThreadId?: () => string | undefined;
};

export const useOnboardingAgent = ({ getThreadId }: UseOnboardingAgentParams = {}) => {
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isFirstOpen, setIsFirstOpen] = useState(false);
    const [isPanelExpanded, setIsPanelExpanded] = useState(false);
    const [shouldAnimatePanelPosition, setShouldAnimatePanelPosition] = useState(false);
    const [shouldAnimateTriggerPosition, setShouldAnimateTriggerPosition] = useState(false);
    const [triggerPosition, setTriggerPosition] = useState(() =>
        resolveTriggerAnchorToPosition(createDefaultTriggerAnchor()),
    );
    const [panelPlacement, setPanelPlacement] = useState(() => resolvePanelPlacement(triggerPosition));
    const [dragPreviewPosition, setDragPreviewPosition] = useState<OnboardingAgentViewportPosition | null>(null);
    const triggerAnchorRef = useRef<OnboardingAgentTriggerAnchor>(createDefaultTriggerAnchor());
    const triggerAnchorBeforeExpandRef = useRef<OnboardingAgentTriggerAnchor | null>(null);
    const panelMountRef = useRef<HTMLDivElement>(null);

    const getCurrentPanelHeight = useCallback(
        (): number => (isPanelExpanded ? getExpandedPanelHeight() : getCompactPanelHeight()),
        [isPanelExpanded],
    );

    /** Single entry point for moving the trigger: the anchor stays the source of truth, pixels are derived. */
    const applyTriggerAnchor = useCallback((anchor: OnboardingAgentTriggerAnchor, panelHeight: number): void => {
        const position = resolveTriggerAnchorToPosition(anchor);

        triggerAnchorRef.current = anchor;
        setTriggerPosition(position);
        setPanelPlacement(resolvePanelPlacement(position, panelHeight));
    }, []);

    const handleTriggerDragMove = useCallback((position: OnboardingAgentViewportPosition): void => {
        setShouldAnimateTriggerPosition(false);
        setShouldAnimatePanelPosition(false);
        setDragPreviewPosition(position);
    }, []);

    const handleTriggerDragEnd = useCallback(
        (position: OnboardingAgentViewportPosition): void => {
            setDragPreviewPosition(null);
            setShouldAnimateTriggerPosition(false);
            setShouldAnimatePanelPosition(false);
            applyTriggerAnchor(createTriggerAnchorFromPosition(position), getCurrentPanelHeight());
            onboardingAgentTrackingService.trackDrag(getThreadId?.());
        },
        [applyTriggerAnchor, getCurrentPanelHeight, getThreadId],
    );

    const handleOpenPanel = useCallback(() => {
        const shouldShowFirstOpenGreeting = onboardingAgentPreferencesService.isFirstOpen();

        setShouldAnimatePanelPosition(false);
        setShouldAnimateTriggerPosition(false);
        setPanelPlacement(resolvePanelPlacement(triggerPosition));
        setIsFirstOpen(shouldShowFirstOpenGreeting);
        if (shouldShowFirstOpenGreeting) {
            void onboardingAgentPreferencesService.markOpened();
        }
        onboardingAgentTrackingService.trackChatbotOpen(getThreadId?.());
        setIsPanelOpen(true);
    }, [getThreadId, triggerPosition]);

    const handleClosePanel = useCallback(() => {
        setIsPanelOpen(false);
        setShouldAnimatePanelPosition(false);
        setShouldAnimateTriggerPosition(false);
    }, []);

    const handlePanelExited = useCallback((): void => {
        const anchorBeforeExpand = triggerAnchorBeforeExpandRef.current;

        triggerAnchorBeforeExpandRef.current = null;
        setIsPanelExpanded(false);
        // Closing while expanded is still a collapse, so the trigger goes back to where the user left it.
        if (anchorBeforeExpand) {
            setShouldAnimateTriggerPosition(true);
            applyTriggerAnchor(anchorBeforeExpand, getCompactPanelHeight());
        }
    }, [applyTriggerAnchor]);

    const handleToggleExpand = useCallback((): void => {
        if (!isPanelOpen) {
            return;
        }

        setShouldAnimatePanelPosition(true);

        if (!isPanelExpanded) {
            triggerAnchorBeforeExpandRef.current = triggerAnchorRef.current;
            applyTriggerAnchor(createDefaultTriggerAnchor(), getExpandedPanelHeight());
            setIsPanelExpanded(true);
            onboardingAgentTrackingService.trackExpand(true, getThreadId?.());
            return;
        }

        const restoredAnchor = triggerAnchorBeforeExpandRef.current ?? triggerAnchorRef.current;
        triggerAnchorBeforeExpandRef.current = null;
        applyTriggerAnchor(restoredAnchor, getCompactPanelHeight());
        setIsPanelExpanded(false);
        onboardingAgentTrackingService.trackExpand(false, getThreadId?.());
    }, [applyTriggerAnchor, getThreadId, isPanelExpanded, isPanelOpen]);

    useEffect(() => {
        const handleResize = (): void => {
            setShouldAnimatePanelPosition(false);
            setShouldAnimateTriggerPosition(false);
            applyTriggerAnchor(triggerAnchorRef.current, getCurrentPanelHeight());
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, [applyTriggerAnchor, getCurrentPanelHeight]);

    const visibleTriggerPosition = dragPreviewPosition ?? triggerPosition;
    const visiblePanelPlacement = dragPreviewPosition
        ? resolvePanelPlacement(dragPreviewPosition, getCurrentPanelHeight())
        : panelPlacement;

    return {
        isPanelOpen,
        isFirstOpen,
        isPanelExpanded,
        shouldAnimatePanelPosition,
        shouldAnimateTriggerPosition,
        panelMountRef,
        triggerPosition: visibleTriggerPosition,
        panelPlacement: visiblePanelPlacement,
        handleOpenPanel,
        handleClosePanel,
        handlePanelExited,
        handleToggleExpand,
        handleTriggerDragMove,
        handleTriggerDragEnd,
    };
};
